import { useCallback, useEffect, useRef, useState } from 'react'

import Loading from '@/components/Loading'
import { SessionRecordingBoundary } from '@/components/molecules/shared/sensitive-analytics'
import { SupportChat } from '@/components/molecules/shared/support-chat'
import { Toaster } from '@/components/ui/toaster'
import { AddressEntity } from '@/entities/address.entity'
import { CompanyEntity } from '@/entities/company.entity'
import { tracking } from '@/features/analytics'
import { isAnalyticsActive } from '@/features/analytics/client/analytics-mode'
import { PageViewTracker } from '@/features/analytics/client/page-view-tracker'
import { BillingHealthBanner } from '@/features/billing/ui/billing-health-banner'
import { isOnboardingPath } from '@/features/onboarding/lib/step-routes'
import { PlatformStatusBanner } from '@/features/platform-status/ui/platform-status-banner'
import { ViewportWithBanner } from '@/features/platform-status/ui/viewport-with-banner'
import { useMobile } from '@/hooks/use-mobile'
import { whenCustomerQueriesSettle } from '@/hooks/useCustomerQuery'
import useEffectAsync from '@/hooks/useEffectAsync'
import { useAppAuth, useAppSignOut, useAppUser } from '@/lib/auth/client'
import {
  CustomerBootstrapConfigurationError,
  CustomerBootstrapRequestError,
  fetchCustomerBootstrap,
  type CustomerBootstrapPayload,
} from '@/lib/customer-bootstrap'
import dynamic from '@/lib/navigation/dynamic'
import { usePathname, useRenderedPathname, useSearchParams } from '@/lib/navigation/navigation'
import { useRouter } from '@/lib/navigation/router'
import { reportError } from '@/spa/sentry'
import { isCacheStale, useGeneralStore } from '@/store/useGeneralStore'
import { OnboardingStatus } from '@/types/enums'
import { isPageTeardownInProgress } from '@/utils/browser/page-teardown'
import { isNavigationCancellationError } from '@/utils/router/navigation-errors'
import { applySentryUserContext, clearSentryUserContext } from '@/utils/sentry/user-context'

import { DashboardShell } from '../dashboard-shell'

import { CustomerNavbar } from './CustomerNavbar'
import { CustomerSidebar } from './CustomerSidebar'
import { useParallelBootstrapPrefetch } from './parallel-bootstrap-prefetch'
import { readSignUpSentFor, recordSignUpSent, signUpTrigger } from './sign-up-conversion'

// MixpanelInit (and mixpanel-browser with it, 123KB gz) loads only once the
// page's first reads have answered and the browser is idle (`afterPageData`),
// so on a cold load it never competes with the page for the network. Tracks
// made before then are queued by the analytics service and flushed by
// MixpanelInit's `loaded` callback. shouldLoadMixpanel (inside the component)
// still excludes admin routes and non-production environments. ssr:false
// because Mixpanel is browser-only. No loading fallback — the component always
// renders null.
const MixpanelInit = dynamic(() => import('@/components/molecules/shared/mixpanel-init').then((m) => m.MixpanelInit), {
  ssr: false,
})

const ONBOARDING_ROUTE = '/onboarding'

/**
 * Runs `run` once the page's first reads have answered (no customer query in flight) and the
 * browser is idle, for the shell's extras that must not compete with the page on a cold load.
 * Returns a cancel.
 */
function afterPageData(run: () => void): () => void {
  let cancelled = false
  let cancelIdle = () => {}
  void whenCustomerQueriesSettle().then(() => {
    if (cancelled) return
    if ('requestIdleCallback' in window) {
      const idleId = window.requestIdleCallback(run, { timeout: 1200 })
      cancelIdle = () => window.cancelIdleCallback(idleId)
    } else {
      const timeoutId = globalThis.setTimeout(run, 250)
      cancelIdle = () => globalThis.clearTimeout(timeoutId)
    }
  })
  return () => {
    cancelled = true
    cancelIdle()
  }
}

// Routes an unfinished-onboarding customer is allowed to sit on without being
// bounced back to the wizard. They render inside DashboardShell like any other
// customer page — see the render gate at the bottom of CustomerLayout.
const ONBOARDING_BYPASS_ROUTES = ['/payment-success']

/** `sign_in` fires once per auth session; the marker lives in localStorage when it can. */
const SIGN_IN_SENT_STORAGE_KEY = 'IsActiveSessionSignedIn'
let signInSentInDocument: string | null = null

function isOnboardingBypassRoute(pathname: string) {
  return ONBOARDING_BYPASS_ROUTES.some((route) => pathname.startsWith(route))
}

function areActiveSubscriptionsEqual(
  left: { plan: string; currentPeriodEnd: string | null }[],
  right: { plan: string; currentPeriodEnd: string | null }[],
) {
  if (left.length !== right.length) {
    return false
  }

  return left.every(
    (subscription, index) =>
      subscription.plan === right[index]?.plan && subscription.currentPeriodEnd === right[index]?.currentPeriodEnd,
  )
}

function isSameBootstrapUser(
  left: {
    id: number | null
    userId: string
    email: string | null
    firstname: string | null
    lastname: string | null
    role: string | null
    companyId: string | null
  } | null,
  right: {
    id: number | null
    userId: string
    email: string | null
    firstname: string | null
    lastname: string | null
    role: string | null
    companyId: string | null
  } | null,
) {
  if (!left || !right) {
    return left === right
  }

  return (
    left.id === right.id &&
    left.userId === right.userId &&
    left.email === right.email &&
    left.firstname === right.firstname &&
    left.lastname === right.lastname &&
    left.role === right.role &&
    left.companyId === right.companyId
  )
}

function areEntitiesEqual(left: object | null | undefined, right: object | null | undefined) {
  if (!left || !right) {
    return left === right
  }

  const leftRecord = left as Record<string, unknown>
  const rightRecord = right as Record<string, unknown>
  const keys = new Set([...Object.keys(leftRecord), ...Object.keys(rightRecord)])
  for (const key of keys) {
    if (leftRecord[key] !== rightRecord[key]) {
      return false
    }
  }

  return true
}

function AnalyticsSuperPropertySync() {
  const companyDetail = useGeneralStore((state) => state.companyDetail)

  useEffect(() => {
    if (!companyDetail?.company_id) return
    tracking.registerSuperProperties({
      company_id: companyDetail.company_id,
      company_type: companyDetail.company_type,
      formation_state: companyDetail.formation_state,
      company_source: companyDetail.source,
    })
  }, [companyDetail?.company_id, companyDetail?.company_type, companyDetail?.formation_state, companyDetail?.source])

  return null
}

function formatLegalCompanyName(company: CompanyEntity | null | undefined): string | null {
  if (!company?.business_name) return null
  return [company.business_name, company.designator].filter(Boolean).join(' ')
}

export function CustomerLayout(props: React.PropsWithChildren) {
  const route = useRouter()
  const routeRef = useRef(route)
  // Kept current via an effect (not a render-time assignment) so render stays
  // pure; every reader below only ever reads `.current` from inside a later
  // effect or callback, never synchronously during this same render.
  useEffect(() => {
    routeRef.current = route
  })
  const { userId, sessionId, isLoaded } = useAppAuth()
  const signOut = useAppSignOut()
  const pathname = usePathname() ?? ''
  const renderedPagePathname = useRenderedPathname()
  const searchParams = useSearchParams()
  const isSignedUp = searchParams?.get('signed-up') === '1'
  const isMobile = useMobile()

  // Prime an opted-in page's data IN PARALLEL with the bootstrap fetch below,
  // so its useCustomerQuery cache is warm by the time this layout's loading gate
  // opens and the page mounts — turning two serial round trips (bootstrap → page
  // data) into one parallel wave. Fire-and-forget; see parallel-bootstrap-prefetch.
  useParallelBootstrapPrefetch({ isLoaded, userId, pathname })

  const [isLoading, setIsLoading] = useState(true)
  const [isOnboarding, setIsOnboarding] = useState(false)
  const { user } = useAppUser()
  const userRef = useRef(user)
  useEffect(() => {
    userRef.current = user
  })
  const setStoreState = useGeneralStore((state) => state.setStoreState)
  const setCustomerBootstrapCache = useGeneralStore((state) => state.setCustomerBootstrapCache)
  const customerBootstrapCache = useGeneralStore((state) => state.customerBootstrapCache)
  const companyDetail = useGeneralStore((state) => state.companyDetail)
  const companyAddressDetail = useGeneralStore((state) => state.companyAddressDetail)
  const customerBootstrapCacheRef = useRef(customerBootstrapCache)
  const companyDetailRef = useRef(companyDetail)
  const companyAddressDetailRef = useRef(companyAddressDetail)
  // Kept current via an effect (not render-time assignment) for the same
  // purity reason as routeRef above — every reader is an effect or callback.
  useEffect(() => {
    customerBootstrapCacheRef.current = customerBootstrapCache
    companyDetailRef.current = companyDetail
    companyAddressDetailRef.current = companyAddressDetail
  })
  const prefetchedRoutes = useRef(new Set<string>())
  // 'idle' → no redirect sent; 'sent' → bounced them to /onboarding once;
  // 'revalidated' → they came back anyway and we refetched the status, so stop
  // redirecting. Bounds the redirect at one attempt plus one revalidation.
  const onboardingRedirectRef = useRef<'idle' | 'sent' | 'revalidated'>('idle')
  // Mirrors the 'revalidated' phase into state: the render gate below has to
  // stop holding <Loading /> when we give up, and a ref mutation alone does not
  // schedule the re-render that would let it notice.
  const [onboardingRedirectExhausted, setOnboardingRedirectExhausted] = useState(false)

  useEffect(() => {
    if (user) {
      applySentryUserContext({
        userId: user.id,
        email: user.emailAddresses[0]?.emailAddress ?? null,
        role: 'customer',
        firstName: user.firstName ?? null,
        lastName: user.lastName ?? null,
        metadata: {
          scope: 'customer_portal',
          current_path: pathname,
        },
      })
    } else {
      clearSentryUserContext()
    }
  }, [pathname, user])

  const sendSignInEvent = useCallback(() => {
    if (!userRef.current) {
      return
    }
    // Private mode / blocked site data makes localStorage throw; analytics
    // must never break the layout, so fall back to the in-document marker.
    let sentFor: string | null = signInSentInDocument
    try {
      sentFor = window.localStorage.getItem(SIGN_IN_SENT_STORAGE_KEY) ?? signInSentInDocument
    } catch {
      // keep the in-document marker
    }
    if (sentFor === sessionId) {
      return
    }

    const method = userRef.current.externalAccounts[0]?.provider || 'email'
    tracking.signIn({ method })

    signInSentInDocument = sessionId || ''
    try {
      window.localStorage.setItem(SIGN_IN_SENT_STORAGE_KEY, sessionId || '')
    } catch {
      // the in-document marker still holds
    }
  }, [sessionId])

  const sendSignUpEvent = useCallback(() => {
    const currentUser = userRef.current
    if (!currentUser) {
      return
    }
    let storage: Storage | null = null
    try {
      storage = window.localStorage
    } catch {
      storage = null
    }
    const trigger = signUpTrigger({
      userId: currentUser.id,
      createdAt: currentUser.createdAt,
      signedUpQuery: isSignedUp,
      alreadySentFor: readSignUpSentFor(storage),
      nowMs: Date.now(),
    })
    if (!trigger) {
      return
    }

    // `?signed-up=1` keeps its provider-derived method; the recent-account path
    // is the email signup (design §5.2).
    const method = trigger === 'query' ? currentUser.externalAccounts[0]?.provider || 'email' : 'email'
    tracking.signUp({ method })
    recordSignUpSent(currentUser.id, storage)
  }, [isSignedUp])

  useEffect(() => {
    // Only in production or a staging capture tab (`analyticsMode`), never on admin pages
    if (!isAnalyticsActive() || routeRef.current.pathname.startsWith('/admin')) {
      return
    }

    tracking.setUser({
      user_id: user?.id || '',
      email: user?.emailAddresses[0]?.emailAddress,
      phone: user?.primaryPhoneNumber?.phoneNumber,
      first_name: user?.firstName || undefined,
      last_name: user?.lastName || undefined,
    })

    sendSignInEvent()
    sendSignUpEvent()
  }, [user, sendSignInEvent, sendSignUpEvent])

  const hydrateCustomerState = useCallback(
    async (payload: CustomerBootstrapPayload) => {
      if (!payload.user) {
        throw new Error('Customer bootstrap payload is missing user data')
      }
      if (useGeneralStore.getState().customerBootstrapStatus !== 'loaded') {
        setStoreState('customerBootstrapStatus', 'loaded')
      }

      const currentStoreUser = useGeneralStore.getState().user as typeof payload.user | undefined
      if (!currentStoreUser || !isSameBootstrapUser(currentStoreUser, payload.user)) {
        setStoreState('user', payload.user)
      }
      const currentBootstrapCache = customerBootstrapCacheRef.current
      const shouldUpdateBootstrapCache =
        !isSameBootstrapUser(currentBootstrapCache.user, payload.user) ||
        currentBootstrapCache.onboardingStatus !== payload.onboardingStatus ||
        !areActiveSubscriptionsEqual(currentBootstrapCache.activeSubscriptions, payload.activeSubscriptions) ||
        isCacheStale(currentBootstrapCache.lastFetchedAt)

      if (shouldUpdateBootstrapCache) {
        setCustomerBootstrapCache({
          user: payload.user,
          onboardingStatus: payload.onboardingStatus,
          activeSubscriptions: payload.activeSubscriptions,
          hasWireTransferFullSuite: payload.hasWireTransferFullSuite ?? false,
        })
      }

      applySentryUserContext({
        userId: userId || null,
        internalUserId: payload.user.id ? String(payload.user.id) : null,
        email: payload.user.email || userRef.current?.emailAddresses?.[0]?.emailAddress || null,
        role: payload.user.role || 'customer',
        companyId: payload.user.companyId || null,
        firstName: payload.user.firstname || userRef.current?.firstName || null,
        lastName: payload.user.lastname || userRef.current?.lastName || null,
        metadata: {
          onboarding_status: payload.onboardingStatus ?? null,
          scope: 'customer_portal_bootstrap',
        },
      })

      if (payload.company) {
        const companyEntity = new CompanyEntity(payload.company)
        if (!areEntitiesEqual(companyDetailRef.current, companyEntity)) {
          setStoreState('companyDetail', companyEntity)
        }

        applySentryUserContext({
          userId: userId || null,
          internalUserId: payload.user.id ? String(payload.user.id) : null,
          email: payload.user.email || userRef.current?.emailAddresses?.[0]?.emailAddress || null,
          role: payload.user.role || 'customer',
          companyId: companyEntity.company_id || null,
          companyName: formatLegalCompanyName(companyEntity),
          firstName: payload.user.firstname || userRef.current?.firstName || null,
          lastName: payload.user.lastname || userRef.current?.lastName || null,
          metadata: {
            onboarding_status: payload.onboardingStatus ?? null,
            company_source: companyEntity.source ?? null,
            scope: 'customer_portal_company',
          },
        })

        if (!routeRef.current.pathname.startsWith('/admin')) {
          tracking.setUser({
            user_id: payload.user.userId || '',
            email: payload.user.email || undefined,
            phone: userRef.current?.primaryPhoneNumber?.phoneNumber || undefined,
            first_name: payload.user.firstname || undefined,
            last_name: payload.user.lastname || undefined,
            onboarding_status: payload.onboardingStatus || undefined,
            company_source: companyEntity.source || undefined,
            company_type: companyEntity.company_type || undefined,
            formation_state: companyEntity.formation_state || undefined,
          })
        }
      } else if (!routeRef.current.pathname.startsWith('/admin')) {
        tracking.setUser({
          user_id: payload.user.userId || '',
          email: payload.user.email || undefined,
          phone: userRef.current?.primaryPhoneNumber?.phoneNumber || undefined,
          first_name: payload.user.firstname || undefined,
          last_name: payload.user.lastname || undefined,
          onboarding_status: payload.onboardingStatus || undefined,
        })
      }

      if (payload.primaryAddress) {
        const addressEntity = new AddressEntity(payload.primaryAddress)
        if (!areEntitiesEqual(companyAddressDetailRef.current, addressEntity)) {
          setStoreState('companyAddressDetail', addressEntity)
        }
      }

      if (payload.onboardingStatus === OnboardingStatus.Completed) {
        setIsOnboarding(false)
        setIsLoading(false)

        if (isOnboardingPath(routeRef.current.pathname)) {
          await routeRef.current.replace('/').catch((err) => {
            if (!isNavigationCancellationError(err)) throw err
          })
        }
        return
      }

      setIsOnboarding(true)
      setTimeout(() => setIsLoading(false), 500)
      // The redirect to /onboarding is owned by the pathname-keyed effect below,
      // not issued once from here — see the comment on that effect.
    },

    [setCustomerBootstrapCache, setStoreState, userId],
  )

  useEffectAsync(async () => {
    // Wait for BetterAuth to be fully loaded before attempting to get tokens
    if (!isLoaded) {
      return
    }

    if (!userId) {
      return
    }

    const bootstrapCache = customerBootstrapCacheRef.current
    const hasFreshBootstrapCache =
      bootstrapCache.user &&
      // Only this account's cache: one left by the previous account in this tab is never fresh.
      bootstrapCache.user.userId === userId &&
      !isCacheStale(bootstrapCache.lastFetchedAt) &&
      bootstrapCache.onboardingStatus &&
      (bootstrapCache.onboardingStatus !== OnboardingStatus.Completed ||
        Boolean(companyDetailRef.current?.company_id || companyAddressDetailRef.current?.id))

    if (hasFreshBootstrapCache) {
      await hydrateCustomerState({
        user: bootstrapCache.user,
        onboardingStatus: bootstrapCache.onboardingStatus,
        company: companyDetailRef.current?.company_id ? companyDetailRef.current : null,
        primaryAddress: companyAddressDetailRef.current?.id ? companyAddressDetailRef.current : null,
        activeSubscriptions: bootstrapCache.activeSubscriptions,
      })
      return
    }

    let payload: CustomerBootstrapPayload
    try {
      payload = await fetchCustomerBootstrap(userId)
    } catch (error) {
      // Pages read this to tell a failed read from an account with no company:
      // both leave `companyDetail` empty.
      setStoreState('customerBootstrapStatus', 'failed')
      // On /onboarding, the page has its own lighter bootstrap endpoint.
      // Don't crash the layout — let the onboarding page handle it.
      if (error instanceof CustomerBootstrapRequestError) {
        if (isOnboardingPath(routeRef.current.pathname)) {
          setIsOnboarding(true)
          setIsLoading(false)
          return
        }
        if (error.status === 401) {
          await signOut()
          return
        }
      }
      // Every other failure used to rethrow. Nothing above this catches it —
      // `useEffectAsync` drops the promise and a React error boundary cannot see
      // a rejected one — so it became an unhandled rejection carrying no context,
      // and `setIsLoading(false)` never ran: the customer sat on <Loading /> for
      // the rest of the session (NONRESIDENT-DB). Report it WITH context and
      // drop the gate, on the same reasoning the onboarding redirect already
      // applies below — an indefinite spinner is worse than showing the page,
      // and the shell is what provides SidebarProvider.
      //
      // One exception, and only one: a dropped request on a page that is
      // already unloading or hidden. The browser cancels its own in-flight
      // fetches on the way out and reports that cancellation as the same bare
      // TypeError a real outage produces, so the report describes a customer
      // who has already left and names no defect anyone could fix
      // (NONRESIDENT-DM — 43 events, one release, one environment, ~2 per
      // session across 30 short-lived sessions). Every HTTP status still
      // reports, every unexpected error still reports, and a dropped request on
      // a live page still reports — those are the ones that mean something.
      const isUnreportableTeardownAbort =
        error instanceof CustomerBootstrapRequestError && error.isNetworkFailure && isPageTeardownInProgress()

      if (!isUnreportableTeardownAbort) {
        reportError(error, {
          tags: {
            component: 'customer-layout',
            operation: 'bootstrap',
            // A configuration fault never reached anyone, so it has no HTTP
            // status to name (of-3 C2); check it before the parent class.
            bootstrap_failure:
              error instanceof CustomerBootstrapConfigurationError
                ? 'configuration'
                : error instanceof CustomerBootstrapRequestError
                  ? error.isNetworkFailure
                    ? 'network'
                    : `http_${error.status}`
                  : 'unexpected',
          },
        })
      }
      // MUST return: `payload` is unassigned on this path, and the hydrate call
      // below would dereference it.
      setIsLoading(false)
      return
    }
    await hydrateCustomerState(payload)
  }, [hydrateCustomerState, isLoaded, setStoreState, signOut, userId])

  // Keep an unfinished-onboarding customer on /onboarding, keyed on the current
  // pathname rather than fired once from hydrateCustomerState. Two reasons:
  // the one-shot version only ran on the bootstrap pass, so a later client-side
  // navigation left isOnboarding true on a route that never redirects; and it
  // raced the 500ms loading gate, so a slow replace() let the previous page —
  // typically the dashboard at "/" — render inside the shell-less onboarding
  // branch. Both ended in "useSidebar must be used within a SidebarProvider."
  // (NONRESIDENT-CX). With the redirect guaranteed to be in flight, the render
  // gate below can safely hold <Loading /> instead of mounting that page.
  useEffect(() => {
    if (isOnboarding !== true) {
      onboardingRedirectRef.current = 'idle'
      setOnboardingRedirectExhausted(false)
      return
    }

    const currentPath = route.pathname
    if (isOnboardingPath(currentPath) || isOnboardingBypassRoute(currentPath)) {
      return
    }

    // Being back here after we already sent this customer to /onboarding means
    // the wizard disagreed with our cached status and bounced them back — the
    // post-payment path does exactly that, because `isOnboarding` is written
    // only by the bootstrap effect above, whose deps are stable across an SPA
    // navigation. Redirecting again would loop forever. Re-derive the status
    // from the server instead, and if even that does not settle it, stop
    // redirecting and let the page render inside the shell (safe — the shell is
    // what provides SidebarProvider). Review finding on PR #627.
    if (onboardingRedirectRef.current !== 'idle') {
      if (onboardingRedirectRef.current === 'sent' && userId) {
        onboardingRedirectRef.current = 'revalidated'
        setOnboardingRedirectExhausted(true)
        fetchCustomerBootstrap(userId, true)
          .then((payload) => hydrateCustomerState(payload))
          .catch((err) => {
            // A 401 here is a session that quietly expired while the customer
            // sat on the page, not a defect: it resolves itself the moment they
            // sign in again, and reporting it at error level only buries the
            // failures that need acting on (NONRESIDENT-DW). The primary
            // bootstrap catch above has excluded 401 all along; this call site
            // had no status filter at all.
            //
            // Reporting is the ONLY thing that changes. This path deliberately
            // does not sign anyone out — that belongs to the bootstrap effect,
            // which owns the session boundary, and bolting auth behaviour onto
            // a telemetry filter is a separate decision.
            if (err instanceof CustomerBootstrapRequestError && err.status === 401) {
              return
            }
            reportError(err, {
              tags: { component: 'customer-layout', operation: 'onboarding_status_revalidate' },
            })
          })
      }
      return
    }

    onboardingRedirectRef.current = 'sent'
    routeRef.current.replace(ONBOARDING_ROUTE).catch((err) => {
      if (isNavigationCancellationError(err)) return
      // The render gate below holds <Loading /> until this navigation lands, so
      // a rejection here strands the customer on a spinner forever. Rethrowing
      // from a floating promise would make that silent — no React boundary sees
      // it. Report it instead, so a stuck spinner is at least visible.
      // (Review finding on PR #627.)
      reportError(err, {
        tags: { component: 'customer-layout', operation: 'onboarding_redirect' },
      })
    })
  }, [hydrateCustomerState, isOnboarding, route.pathname, userId])

  useEffect(() => {
    if (isLoading || isOnboarding) {
      return
    }

    const currentPath = route.pathname
    const stateTaxRoute = '/state-filings'
    const prefetchCandidatesByRoute: Record<string, string[]> = {
      '/': ['/federal-tax', stateTaxRoute, '/services'],
      '/federal-tax': ['/', stateTaxRoute, '/company'],
      [stateTaxRoute]: ['/federal-tax', '/', '/company'],
      '/company': ['/', '/services', '/mailroom'],
      '/services': ['/accounting', '/mailroom', '/banking'],
      '/accounting': ['/services', '/banking', '/mailroom'],
      '/banking': ['/services', '/accounting', '/mailroom'],
      '/mailroom': ['/services', '/accounting', '/banking'],
    }
    const candidates = prefetchCandidatesByRoute[currentPath] ?? []
    if (candidates.length === 0) {
      return
    }

    const prefetchRoutes = () => {
      candidates.forEach((candidate) => {
        if (candidate === currentPath || prefetchedRoutes.current.has(candidate)) {
          return
        }

        prefetchedRoutes.current.add(candidate)
        routeRef.current.prefetch(candidate).catch(() => {
          prefetchedRoutes.current.delete(candidate)
        })
      })
    }

    // After the page's own data: on a cold load these chunks would otherwise share the network
    // with the page's reads.
    return afterPageData(prefetchRoutes)
  }, [companyDetail?.formation_state, isLoading, isOnboarding, route.pathname])

  const [loadMixpanel, setLoadMixpanel] = useState(false)
  useEffect(() => {
    if (isLoading || loadMixpanel) return
    return afterPageData(() => setLoadMixpanel(true))
  }, [isLoading, loadMixpanel])

  if (isLoading) {
    return <Loading />
  }

  // The branch below wraps whatever page the router's outlet renders, so it is
  // chosen from that page, not from route.pathname: the location moves the
  // moment a navigation starts, while the code-split next page is still
  // loading and the outlet still shows the previous one. Reading the location
  // here put the dashboard into the shell-less wizard branch for that window
  // on every redirect to /onboarding (NONRESIDENTTAX-FA).
  const renderedPathname = renderedPagePathname ?? route.pathname

  // Only the onboarding wizard itself is built to render without DashboardShell.
  // Every other customer page composes `PageHeader sticky`, whose SidebarTrigger
  // calls useSidebar() — and DashboardShell is the sole SidebarProvider. So a
  // customer stuck mid-onboarding on any other route waits on <Loading /> until
  // the redirect effect above lands them on /onboarding, and an explicit bypass
  // route (e.g. /payment-success) falls through to the normal shell below.
  // Only hold the spinner while a redirect is actually in flight. Once the
  // effect above has given up (the wizard kept sending them back), fall through
  // to the shell instead — an indefinite spinner is worse than showing the page,
  // and inside DashboardShell the sticky PageHeader has its SidebarProvider.
  if (
    isOnboarding &&
    !isOnboardingPath(renderedPathname) &&
    !isOnboardingBypassRoute(renderedPathname) &&
    !onboardingRedirectExhausted
  ) {
    return <Loading />
  }

  if (isOnboarding && isOnboardingPath(renderedPathname)) {
    return (
      <>
        {loadMixpanel ? <MixpanelInit /> : null}
        {!isMobile ? <SupportChat userId={user?.id} /> : null}
        <AnalyticsSuperPropertySync />
        <ViewportWithBanner banner={<PlatformStatusBanner />}>
          <SessionRecordingBoundary pathname={route.pathname}>{props.children}</SessionRecordingBoundary>
        </ViewportWithBanner>
        {/* After the page, so its effect runs once the page has set its title. */}
        <PageViewTracker />
      </>
    )
  }

  return (
    <>
      {loadMixpanel ? <MixpanelInit /> : null}
      <SupportChat userId={user?.id} />
      <AnalyticsSuperPropertySync />
      <Toaster />

      <BillingHealthBanner />
      <DashboardShell navbar={<CustomerNavbar />} sidebar={<CustomerSidebar />} banner={<PlatformStatusBanner />}>
        <SessionRecordingBoundary pathname={route.pathname}>{props.children}</SessionRecordingBoundary>
      </DashboardShell>
      {/* After the page, so its effect runs once the page has set its title. */}
      <PageViewTracker />
    </>
  )
}
